import React, { useEffect, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { Outlet, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import PageErrorBoundary from "../../components/PageErrorBoundary";
import { useGetUserQuery } from "../../state/api";
import { getTheme } from "../../theme/modernTheme";

export const SIDEBAR_WIDTH = 216;

const Layout = () => {
  const isNonMobile = useMediaQuery("(min-width: 600px)");
  const [isSidebarOpen, setIsSidebarOpen] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 600px)").matches : true
  );
  const userId = useSelector((state) => state.global.userId);
  const mode = useSelector((state) => state.global.mode);
  const location = useLocation();
  const { data } = useGetUserQuery(userId);

  const currentTheme = getTheme(mode);
  const desktopSidebarOpen = isNonMobile && isSidebarOpen;
  const mainOffset = desktopSidebarOpen ? SIDEBAR_WIDTH : 0;

  useEffect(() => {
    if (!isNonMobile) {
      setIsSidebarOpen(false);
    }
  }, [isNonMobile]);

  useEffect(() => {
    const isLight = mode === "light";
    document.body.classList.remove("light-theme", "dark-theme");
    document.body.classList.add(isLight ? "light-theme" : "dark-theme");
    document.documentElement.setAttribute("data-theme", isLight ? "light" : "dark");
    return () => {
      document.body.classList.remove("light-theme", "dark-theme");
      document.documentElement.removeAttribute("data-theme");
    };
  }, [mode]);

  return (
    <Box
      className={`crm-app-layout${mode === "light" ? " light-theme" : ""}`}
      sx={{
        width: "100%",
        minHeight: "100vh",
        background: currentTheme.colors.gradient.background,
        overflowX: "hidden",
      }}
    >
      {desktopSidebarOpen && (
        <Box
          component="aside"
          aria-label="Ana menü"
          className="crm-sidebar-fixed crm-sidebar"
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 1200,
            width: SIDEBAR_WIDTH,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            color: currentTheme.colors.text.secondary,
            background: currentTheme.colors.surface.primary,
            backdropFilter: currentTheme.effects.backdrop,
            borderRight: `1px solid ${currentTheme.colors.border.primary}`,
            boxShadow: currentTheme.shadows.glass,
            boxSizing: "border-box",
            overflow: "hidden",
          }}
        >
          <Sidebar
            embedded
            user={data || {}}
            isNonMobile={isNonMobile}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
          />
        </Box>
      )}

      {!isNonMobile && (
        <Sidebar
          user={data || {}}
          isNonMobile={isNonMobile}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
      )}

      <Box
        component="main"
        className="crm-main-column"
        sx={{
          minHeight: "100vh",
          ml: `${mainOffset}px`,
          width: mainOffset ? `calc(100% - ${mainOffset}px)` : "100%",
          transition: "margin-left 0.2s ease, width 0.2s ease",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          // Sabit alt barlar (manuel sipariş vb.) ana kolonla hizalansın
          "--crm-main-inset-left": `${mainOffset}px`,
        }}
      >
        <Box
          component="header"
          sx={{
            flexShrink: 0,
            position: "relative",
            zIndex: 1100,
            overflow: "visible",
          }}
        >
          <Navbar
            user={data || {}}
            isSidebarOpen={isSidebarOpen}
            setIsSidebarOpen={setIsSidebarOpen}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            overflowX: "hidden",
            px: { xs: 1, sm: 1.5 },
            py: { xs: 0.5, sm: 1 },
          }}
        >
          <PageErrorBoundary
            key={location.pathname}
            title="Sayfa yüklenirken hata oluştu"
            fullPage
          >
            <Outlet />
          </PageErrorBoundary>
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
